// ─────────────────────────────────────────────
//  Departure domain — pure eligibility logic (no Firebase)
//  Owns the rules for WHO may depart and WHAT must happen first:
//  active rooms must be individually reassigned (D-07) and the AIC
//  must hand off before leaving (D-07). DepartureFlow renders this;
//  it never re-derives the rules.
// ─────────────────────────────────────────────
import { isActiveStatus } from "./status";
import { rowClaimsAt } from "./timeline";

// A departed person disappears from every dropdown and reliever list (D-06).
export function filterActiveStaff(staff, rosterDeparted) {
  const departed = rosterDeparted || {};
  return (staff || []).filter((s) => !departed[s.id]);
}

// ── THE DOCTORS WHO ARE ACTUALLY HERE TODAY ──────────────────────────────
//
// Owner, 2026-09-09, on the AIC hand-off modal: *"In this list use the doctors
// from the roster of the day"*. Both pickers in `DepartureFlow` — who takes the
// AIC, and who takes each room — were filled from `staffPool.doctors`, the
// FACILITY'S WHOLE MASTER LIST of anesthesiologists, minus whoever had already
// been marked departed. So the departing AIC was offered colleagues who are not
// in the building: a hand-off to one of them names an absent doctor as the
// person in charge, and a room reassigned to one leaves the room covered on
// paper by somebody who never arrives.
//
// ⚠️ THE AIC COUNTS AS ON THE ROSTER. `roster.supervisors` is the day's doctor
// list, but the AIC is stored separately (`aicId`) and is not necessarily in it;
// omitting them would drop the one doctor who is certainly here.
// `BoardPage.allowedDoctorIds` already unions exactly these two, and this is
// that same rule with a name, so the two cannot drift.
export function rosterDoctorIds(roster) {
  const ids = new Set();
  if (roster?.aicId) ids.add(roster.aicId);
  for (const s of roster?.supervisors || []) if (s && s.id) ids.add(s.id);
  return ids;
}

/**
 * Today's doctors, out of the facility's staff list.
 *
 * ⚠️ IT FALLS BACK TO THE FULL POOL, AND THE CALLER MUST SAY SO. Two cases:
 * a day nobody rostered, and a roster whose ids match no staff record any more.
 * Returning [] for either would leave the AIC hand-off with an empty picker and
 * NO WAY TO DEPART AT ALL — a doctor stuck on the board all night is worse than
 * a list that is too long. So it degrades to the old behaviour and reports it
 * with `fromRoster: false`; a picker that quietly widened would be the same
 * defect this function exists to fix.
 */
export function rosterDoctors(staffDoctors, roster) {
  const pool = Array.isArray(staffDoctors) ? staffDoctors : [];
  const ids = rosterDoctorIds(roster);
  if (ids.size === 0) return { doctors: pool, fromRoster: false };
  const onRoster = pool.filter((d) => d && ids.has(d.id));
  if (onRoster.length === 0) return { doctors: pool, fromRoster: false };
  return { doctors: onRoster, fromRoster: true };
}

// An assignment row still needing coverage: not done/empty/cancelled.
// Fresh grid rows default to "scheduled", which counts as live.
function isLiveAssignment(status) {
  if (status == null || status === "scheduled") return true;
  return isActiveStatus(status);
}

// Rooms this person is currently covering (as attending OR as the AHP).
//
// ⚠️ STATUS IS NOT A CLOCK, AND ASKING ONLY STATUS STRANDED THE DEPARTURE.
// `isLiveAssignment` treats the DEFAULT `"scheduled"` as live — and the sheet
// importer writes exactly that on every row it creates (`dailyImport`), where it
// stays, because nobody walks the board at 20:00 marking finished cases done. So
// a doctor whose last case ended at 15:00 was still "covering 5 rooms" at 20:49,
// and the modal demanded a replacement for five rooms that had nobody in them.
// Reported 2026-08-31 with a screenshot: *"all the cases are done, that mean
// that this doctor should not have rooms."*
//
// `rowClaimsAt` is the SAME predicate the bench and FREE NOW already use to
// release a finished room (commit 086902b) — one definition, so the chart, the
// bench and this modal cannot disagree about when a room lets go of somebody.
// It also answers the other half of the owner's rule — *"the only case is if you
// have a case in a late time you will keep the room"* — because a case still
// running, or one yet to start, keeps claiming.
//
// ⚠️ `nowMin` IS OPTIONAL AND OMITTING IT KEEPS EVERY ROOM. `rowClaimsAt`
// returns true for a null clock, so an un-updated caller behaves exactly as
// before. That is the fail-closed direction: over-demanding a reassignment costs
// a charge nurse one dropdown, under-demanding one strands a live room.
export function activeRoomsFor(person, assignments, nowMin = null) {
  if (!person?.id) return [];
  return (assignments || []).filter(
    (a) =>
      (a.attendingId === person.id || a.ahpId === person.id) &&
      isLiveAssignment(a.status) &&
      rowClaimsAt(a, nowMin)
  );
}

// Leaving with a live room would strand it — reassignment is mandatory.
export function requiresReassignment(person, assignments, nowMin = null) {
  return activeRoomsFor(person, assignments, nowMin).length > 0;
}

// True only when EVERY active room has a non-empty replacement picked.
// This is the hard gate that keeps a departure from stranding a room.
export function allRoomsReassigned(reassignMap, activeRooms) {
  const map = reassignMap || {};
  return (activeRooms || []).every((room) => !!map[room.id]);
}

// The current AIC must hand the designation off before departing (D-07);
// the deployed Phase-3 rule authorizes the aicId move server-side.
export function requiresAicHandoff(person, aicId) {
  return !!person?.id && person.id === aicId;
}
